import { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components/macro';
import { LoadingOverlay } from '../../../components/Loading';
import { paramsEqual, updateParams } from '../../../helpers/urlParams';

import subDays from 'date-fns/subDays';
import Page from '../../../components/Page';
import Tab from '../../../components/Tabs/Tab';
import Tabs from '../../../components/Tabs/Tabs';
import { GetCustomersReason } from '../actions';
import { getGapPageArgs, getNextPageArgs } from '../helpers';
import { State as CustomersState } from '../reducer';
import CustomerTable from './CustomerTable';
import Filters from './Filters';
import ListMessage from './ListMessage';
import Paging from './Paging';

const today = new Date();
const thirtyDaysAgo = subDays(today, 30);

interface MatchParams {
    accountId: string;
}

export interface CustomerListProps extends WithTranslation, RouteComponentProps<MatchParams> {
    getCustomers: (accountId: string, params: URLSearchParams, origin: GetCustomersReason) => void;
    getCustomersCount: (accountId: string) => void;
    customers: CustomersState;
}

class CustomersList extends Component<CustomerListProps> {
    componentDidMount() {
        const { limit, accountId, customers, params } = this.props.customers;
        const urlAccountId = this.props.match.params.accountId;
        // Fetch customers on mount with initial query params
        const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));
        const requestQueryParams = updateParams(locationQueryParams, { limit: limit.toString(), type: 'any' });
        const hasCustomers = customers.length > 0;
        const isSameAccount = accountId === urlAccountId;
        const hasSameParams = paramsEqual(requestQueryParams, params, ['limit', 'starting_after']);
        const reason =
            hasCustomers && isSameAccount && hasSameParams ? GetCustomersReason.refresh : GetCustomersReason.mount;
        this.props.getCustomers(urlAccountId, requestQueryParams, reason);
        this.props.getCustomersCount(urlAccountId);
    }

    componentDidUpdate(prevProps: CustomerListProps, prevState: Readonly<{}>) {
        // Query params have changed, fetch Customer again...

        if (this.props.location.search !== prevProps.location.search || this.props.match.params.accountId !== prevProps.match.params.accountId) {
            const { limit } = this.props.customers;
            const urlAccountId = this.props.match.params.accountId;
            const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));
            const requestQueryParams = updateParams(locationQueryParams, { limit: limit.toString(), type: 'any' });
            this.props.getCustomers(urlAccountId, requestQueryParams, GetCustomersReason.update);
            this.props.getCustomersCount(urlAccountId);
        }
    }

    render() {
        const { t, getCustomers } = this.props;
        const { customers, isLoading, hasNext, isLoadingNext, totalCount } = this.props.customers;
        const accountId = this.props.match.params.accountId;
        const limit = this.props.customers.limit.toString();

        const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));
        const refreshParams = updateParams(locationQueryParams, { limit, type: 'any' });
        const refresh = () => {
            getCustomers(accountId, refreshParams, GetCustomersReason.refresh);
        };

        const nextPageArgs = getNextPageArgs(customers);
        const nextParams = updateParams(locationQueryParams, { ...nextPageArgs, limit, type: 'any' });
        const next = () => {
            getCustomers(accountId, nextParams, GetCustomersReason.next);
        };

        const fillGap = (id: string) => {
            const gapPageArgs = getGapPageArgs(customers, id);
            const gapParams = updateParams(locationQueryParams, { ...gapPageArgs, limit, type: 'any' });
            getCustomers(accountId, gapParams, GetCustomersReason.refresh);
        };
        return (
            <Page title={t('customers.customers.title', { count: totalCount })} hasEnvFrame={true}>
                <Tabs>
                    <Tab path={`/${accountId}/customers`}>
                        {t('customers.menu.all', {
                            count: totalCount,
                        })}
                    </Tab>
                    <Tab path={`/${accountId}/customers?since_datetime=${thirtyDaysAgo.toISOString()}`}>
                        {t('customers.menu.registered_last_30')}
                    </Tab>
                </Tabs>
                <Filters history={this.props.history} location={this.props.location} refresh={refresh} />
                <Wrapper>
                    <CustomerTable customers={customers} accountId={accountId} fillGap={fillGap} />
                    <RelativeGroup>
                        <Paging next={next} isLoading={isLoading} hasNext={hasNext} />
                        <ListMessage
                            customers={customers}
                            isLoading={isLoading}
                            hasNext={hasNext}
                            isLoadingNext={isLoadingNext}
                            accountId={accountId}
                            queryParams={locationQueryParams}
                        />
                        {isLoading && <LoadingOverlay />}
                    </RelativeGroup>
                </Wrapper>
            </Page>
        );
    }
}

export default withTranslation()(CustomersList);

const Wrapper = styled.div`
    position: relative;
    box-sizing: border-box;
`;

const RelativeGroup = styled.div`
    position: relative;
    box-sizing: border-box;
    overflow: hidden;
`;
